import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';

interface WriteOff {
  id: string;
  orderNumber: string;
  dealerName: string | null;
  writtenOffAt: string | null;
  reason: string | null;
  bomMaterialCost: number;
  hasCostSnapshot: boolean;
  bomWrittenOff: boolean;
  bomWrittenOffAt: string | null;
  bomWrittenOffByName: string | null;
}

const fmt = (n: number) => `$${Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function WriteOffsPage() {
  const [rows, setRows] = useState<WriteOff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const data = await api.accounting.getWriteOffs();
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e.message || 'Failed to load write-offs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (row: WriteOff) => {
    setSaving(row.id);
    try {
      await api.accounting.setBomWrittenOff(row.id, !row.bomWrittenOff);
      await load();
    } catch (e: any) {
      alert(e.message || 'Failed to update');
    } finally {
      setSaving(null);
    }
  };

  const totalCost = rows.reduce((s, r) => s + (r.bomMaterialCost || 0), 0);
  const outstandingCost = rows.filter(r => !r.bomWrittenOff).reduce((s, r) => s + (r.bomMaterialCost || 0), 0);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Write-offs (Comped Orders)</h1>
        <p className="text-gray-500 mt-1">
          Orders given away / zeroed out. The materials were still consumed — review each BOM cost and mark it written off once recorded in the books.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Comped Orders</div>
          <div className="text-2xl font-bold text-gray-900">{rows.length}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Total BOM Cost</div>
          <div className="text-2xl font-bold text-gray-900">{fmt(totalCost)}</div>
        </div>
        <div className="bg-white rounded-lg border border-amber-200 p-4">
          <div className="text-xs text-amber-700 uppercase tracking-wide">Not Yet Written Off</div>
          <div className="text-2xl font-bold text-amber-700">{fmt(outstandingCost)}</div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>}

      {!loading && !error && rows.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-500">
          No comped orders yet.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Order', 'Dealer', 'Comped', 'Reason', 'BOM Cost (COGS)', 'BOM Written Off', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Link to={`/orders/${r.id}`} className="text-blue-600 hover:text-blue-800 font-medium">{r.orderNumber}</Link>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-900">{r.dealerName || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{fmtDate(r.writtenOffAt)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={r.reason || ''}>{r.reason || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono font-semibold text-gray-900">
                    {r.hasCostSnapshot ? fmt(r.bomMaterialCost) : <span className="text-xs text-amber-600">no cost snapshot</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {r.bomWrittenOff ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                        ✓ {fmtDate(r.bomWrittenOffAt)}{r.bomWrittenOffByName ? ` · ${r.bomWrittenOffByName}` : ''}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">Pending</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-3">
                      <Link to={`/orders/${r.id}`} className="text-xs text-gray-500 hover:text-gray-700" title="View the order's BOM">View BOM</Link>
                      <button
                        onClick={() => toggle(r)}
                        disabled={saving === r.id}
                        className={`px-3 py-1 text-xs rounded font-medium disabled:opacity-50 ${
                          r.bomWrittenOff
                            ? 'border border-gray-300 text-gray-600 hover:bg-gray-100'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {saving === r.id ? '…' : r.bomWrittenOff ? 'Undo' : 'Mark written off'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
